<template>
  <v-autocomplete
    :value="value"
    :items="items"
    item-text="text"
    item-value="id"
    :filter="filterAccount"
    :label="label || $t('finance.reports2.common.accountPicker.label')"
    :hint="$t('finance.reports2.common.accountPicker.hint')"
    :no-data-text="$t('finance.reports2.common.accountPicker.noResults')"
    :loading="loading"
    :disabled="disabled"
    :clearable="clearable"
    :hide-details="hideDetails"
    :error-messages="errorMessage ? [errorMessage] : []"
    :class="fieldClass"
    @focus="refreshIfStale"
    @change="onChange"
  >
    <template slot="item" slot-scope="data">
      <v-list-tile-content>
        <v-list-tile-title>
          <span class="ap-code">{{ data.item.code }}</span>
          {{ data.item.name }}
          <span v-if="!data.item.is_active" class="ap-inactive">({{ $t("finance.reports2.common.accountPicker.inactive") }})</span>
        </v-list-tile-title>
      </v-list-tile-content>
    </template>
  </v-autocomplete>
</template>

<script>
/*
 * Chart-of-accounts picker for the 2026-10-03 ledger reports.
 *
 * Loads GET accounting/chart-of-accounts into a cache shared by every picker
 * instance and filters locally by code or name, so there is no server search
 * and none of the async-autocomplete pitfalls (memory:
 * vuetify-async-autocomplete). The cache lives CACHE_TTL_MS: a picker that
 * mounts, or is opened (focused), after that reloads the chart, so a leaf
 * provisioned meanwhile (a new driver / centre / employee account) or a
 * renamed / re-activated account shows up without a browser reload. The
 * current list stays on screen while the refresh is in flight.
 *
 *   <account-picker v-model="filters.account_id" leaf-only @change="onAccount" />
 *
 * Props: value (account id), leafOnly (only accounts without children),
 * activeOnly, label, disabled, clearable, hideDetails, fieldClass.
 * Events: input (id | null), change (account row | null), loaded (all rows).
 * Account rows carry id, code, name, name_ar, type, normal_balance, level,
 * parent_id, is_active, has_children, allows_manual_posting.
 */
const CACHE_TTL_MS = 5 * 60 * 1000;

// promise: the last (or in-flight) load; loadedAt: when it resolved (0 while in flight).
let cache = { promise: null, loadedAt: 0 };

function isCacheFresh(now) {
  if (!cache.promise) {
    return false;
  }
  return cache.loadedAt === 0 || now - cache.loadedAt < CACHE_TTL_MS;
}

function loadAccounts(vm, now) {
  const at = now === undefined ? Date.now() : now;
  if (!isCacheFresh(at)) {
    const promise = vm.axios
      .get(vm.api.ACTIONS.ACCOUNTING_CHART_OF_ACCOUNTS)
      .then(function (response) {
        if (cache.promise === promise) {
          cache.loadedAt = Date.now();
        }
        return (response.data && response.data.data) || [];
      })
      .catch(function (error) {
        if (cache.promise === promise) {
          cache = { promise: null, loadedAt: 0 }; // allow a retry on the next mount / open
        }
        throw error;
      });
    cache = { promise: promise, loadedAt: 0 };
  }
  return cache.promise;
}

function normalize(text) {
  return String(text == null ? "" : text)
    .toLowerCase()
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .trim();
}

export default {
  name: "AccountPicker",
  props: {
    value: { type: [Number, String], default: null },
    leafOnly: { type: Boolean, default: false },
    activeOnly: { type: Boolean, default: false },
    label: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    hideDetails: { type: Boolean, default: true },
    fieldClass: { type: String, default: "fin-field" },
  },
  data() {
    return {
      accounts: [],
      shownPromise: null,
      loading: false,
      errorMessage: "",
    };
  },
  computed: {
    isArabic() {
      return !!(this.$i18n && this.$i18n.locale === "ar");
    },
    items() {
      return this.accounts
        .filter(
          function (account) {
            if (this.leafOnly && account.has_children) {
              return false;
            }
            if (this.activeOnly && !account.is_active) {
              return false;
            }
            return true;
          }.bind(this)
        )
        .map(
          function (account) {
            const name = (this.isArabic ? account.name_ar || account.name : account.name || account.name_ar) || "";
            return Object.assign({}, account, {
              name: name,
              text: account.code + " - " + name,
            });
          }.bind(this)
        );
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      const promise = loadAccounts(this);
      if (promise === this.shownPromise) {
        return; // already showing this load
      }
      this.loading = true;
      promise
        .then(
          function (accounts) {
            this.shownPromise = promise;
            this.accounts = accounts;
            this.errorMessage = "";
            this.$emit("loaded", accounts);
          }.bind(this)
        )
        .catch(
          function (error) {
            const forbidden = error && error.response && error.response.status === 403;
            this.errorMessage = forbidden
              ? this.$t("finance.reports2.common.accountPicker.forbidden")
              : this.$t("finance.reports2.common.accountPicker.failedLoad");
            window.getApp.$emit("SHOW_TOAST", this.errorMessage, "red");
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    refreshIfStale() {
      if (!isCacheFresh(Date.now())) {
        this.load();
      }
    },
    filterAccount(item, queryText) {
      const query = normalize(queryText);
      if (!query) {
        return true;
      }
      return (
        String(item.code).indexOf(query) === 0 ||
        normalize(item.name).indexOf(query) !== -1 ||
        normalize(item.name_ar).indexOf(query) !== -1 ||
        String(item.code).indexOf(query) !== -1
      );
    },
    accountById(id) {
      if (id === null || id === undefined || id === "") {
        return null;
      }
      for (let i = 0; i < this.accounts.length; i++) {
        if (String(this.accounts[i].id) === String(id)) {
          return this.accounts[i];
        }
      }
      return null;
    },
    onChange(id) {
      const value = id === undefined || id === "" ? null : id;
      this.$emit("input", value);
      this.$emit("change", this.accountById(value));
    },
  },
};
</script>

<style scoped>
.ap-code {
  direction: ltr;
  unicode-bidi: embed;
  font-weight: 600;
  margin-inline-end: 6px;
}
.ap-inactive {
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}
</style>
